import {DetailsField} from 'components';
import {WorkflowStatus, RegulatoryDocument} from 'types';
import {TFunction} from 'react-i18next';
import type {IDropdownOption} from 'components/FluentUICompat';

export interface BuildRegulatoryDocumentFieldRowsArgs {
	regulatoryDocumentDetails: RegulatoryDocument;
	t: TFunction;
	tPogis: TFunction;
	getWorkflowStatus: TFunction;
	showAudit: boolean;
	isAdmin: boolean;
	reapprovingHistoryMessages: {date: Date; value: string}[];
	areAuditLogsLoading: boolean;
	getCorrectStatusName: (
		regDoc: RegulatoryDocument,
		t: TFunction,
		wfT: TFunction,
	) => string;
	getStatusValue: () => DetailsField['data'];
	getDisassemblyStatusValue: () => DetailsField['data'];
	WorkflowStatusControl: any;
	handleWorkflowStatusChange: (
		_event: React.FormEvent<HTMLDivElement>,
		option?: IDropdownOption,
	) => Promise<void> | void;
	attachmentFaqLine: DetailsField[];
	tDisclaimer?: string;
	isGetexInjected: boolean;
	traceKeywords?: Array<{id: string; name: string}>;
	vehicleCategories?: string[];
}

interface FieldThatSupportsHistory extends DetailsField {
	auditFieldName: string;
	audit: boolean;
}

const createFieldThatSupportsHistory = (
	showAudit: boolean,
	field: Omit<FieldThatSupportsHistory, 'audit'>,
): FieldThatSupportsHistory => ({...field, audit: showAudit});

export const buildRegulatoryDocumentFieldRows = (
	args: BuildRegulatoryDocumentFieldRowsArgs,
): DetailsField[][] => {
	const {
		regulatoryDocumentDetails,
		t,
		tPogis,
		getWorkflowStatus,
		showAudit,
		isAdmin,
		reapprovingHistoryMessages,
		areAuditLogsLoading,
		getCorrectStatusName,
		getStatusValue,
		getDisassemblyStatusValue,
		WorkflowStatusControl,
		handleWorkflowStatusChange,
		attachmentFaqLine,
		tDisclaimer,
		isGetexInjected,
		traceKeywords,
		vehicleCategories,
	} = args;

	const StatusField: DetailsField[] = [];
	const PogisField: DetailsField[] = [];
	const workflowField: DetailsField = isAdmin
		? createFieldThatSupportsHistory(showAudit, {
				label: t('WorkflowStatus'),
				type: 'enumDropdown',
				data: regulatoryDocumentDetails?.workflow?.status,
				enumProps: {
					name: 'WorkflowStatus',
					enumName: 'WorkflowStatus',
					dropdownWidth: 'auto',
					control: WorkflowStatusControl,
					enumType: WorkflowStatus,
					onChange: handleWorkflowStatusChange,
				},
				auditFieldName: 'workflow.status',
				rowNumber: 2,
		  })
		: createFieldThatSupportsHistory(showAudit, {
				label: t('WorkflowStatus'),
				type: 'text',
				data: getCorrectStatusName(
					regulatoryDocumentDetails,
					t,
					getWorkflowStatus,
				),
				auditFieldName: 'workflow.status',
				rowNumber: 2,
		  });

	if (
		[
			WorkflowStatus.Clearing,
			WorkflowStatus.ClearingWithReservations,
			WorkflowStatus.Modified,
		].includes(regulatoryDocumentDetails?.workflow?.status as WorkflowStatus)
	) {
		const StatusFieldMeta: DetailsField = {
			data: '',
			type: 'array',
			label: t('Approver'),
			rowNumber: 2,
		};
		if (
			regulatoryDocumentDetails?.workflow?.status === WorkflowStatus.Modified
		) {
			StatusField.push({
				...StatusFieldMeta,
				data: [regulatoryDocumentDetails?.workflow?.reapprovingVKO?.name],
			});
		} else {
			StatusField.push({
				...StatusFieldMeta,
				data: (regulatoryDocumentDetails.workflow?.clearingVKO ?? []).map(
					x => x.name,
				),
			});
		}
	}

	if (regulatoryDocumentDetails.pogisExists) {
		PogisField.push({
			data: tPogis('yes'),
			type: 'text',
			label: tPogis('AlreadyCommunicated'),
			rowNumber: 3,
		});
		PogisField.push({
			data: (regulatoryDocumentDetails.pogisDocuments ?? []).map(
				x => x.pogisId,
			),
			type: 'array',
			label: tPogis('pogisDocuments'),
			rowNumber: 3,
		});
	}

	const documentReferences =
		regulatoryDocumentDetails?.documentReferences ?? [];

	/**
	 * Filter outgoing references: those that reference other documents
	 * We include references that either have specific documents referenced
	 * or have the "select all" flag set to true.
	 */
	const documentReferencesOutgoing = documentReferences
		.filter(ref => {
			const regulatoryDocs =
				ref?.documentReferenceGroup?.regulatoryDocumentReferences ?? [];
			const hasSelectAll =
				ref?.documentReferenceGroup?.selectAllRegulatoryDocuments ?? false;
			return regulatoryDocs.length > 0 || hasSelectAll;
		})
		.map(ref => ({
			...ref,
			referencedByDocuments: [],
		}));

	/**
	 * Filter incoming references: those that are referenced by other documents
	 * exclude the referenced regulatory document references
	 */
	const documentReferencesIncoming = documentReferences
		.filter(ref => (ref?.referencedByDocuments?.length ?? 0) > 0)
		.map(ref => ({
			...ref,
			requirementReferences: [],
			documentReferenceGroup: {
				...ref.documentReferenceGroup,
				regulatoryDocumentReferences: [],
			},
		}));

	return [
		[
			createFieldThatSupportsHistory(showAudit, {
				data: regulatoryDocumentDetails?.summary,
				type: 'richtext',
				label: t('ShortSummaryLabel'),
				auditFieldName: '$.summary',
				rowNumber: 1,
			}),
			{
				data: reapprovingHistoryMessages,
				type: 'history',
				label: t('ChangeHistoryLabel'),
				isLoading: areAuditLogsLoading,
				rowNumber: 1,
			},
		],
		[
			createFieldThatSupportsHistory(showAudit, {
				data: getStatusValue(),
				type: 'text',
				label: t('StatusLabel'),
				auditFieldName: '$.status',
				rowNumber: 2,
			}),
			...StatusField,
			workflowField,
			...PogisField,
			{
				data: getDisassemblyStatusValue(),
				type: 'text',
				label: t('DisassemblyStatus'),
				rowNumber: 4,
			},
			{
				data: regulatoryDocumentDetails?.documentSource?.name,
				type: 'text',
				label: t('DocumentSourceLabel'),
				rowNumber: 4,
			},
		],
		attachmentFaqLine,
		[
			isGetexInjected
				? {
						data: traceKeywords ?? [],
						type: 'array',
						label: t('TraceKeywordsLabel'),
						rowNumber: 5,
				  }
				: {
						data: regulatoryDocumentDetails.keywords,
						type: 'array',
						label: t('KeywordsLabel'),
						rowNumber: 5,
				  },
		],
		[
			{
				data: regulatoryDocumentDetails?.createdAt,
				type: 'date',
				label: t('CreationDateLabel'),
				rowNumber: 6,
			},
			{
				data: regulatoryDocumentDetails?.modifiedAt,
				type: 'date',
				label: t('ChangeDateLabel'),
				rowNumber: 6,
			},
			createFieldThatSupportsHistory(showAudit, {
				data: regulatoryDocumentDetails?.dateEffective,
				type: 'date',
				label: t('EffectiveDateLabel'),
				auditFieldName: 'dateEffective',
				rowNumber: 1,
			}),
			createFieldThatSupportsHistory(showAudit, {
				data: regulatoryDocumentDetails?.dateExpiration,
				type: 'date',
				label: t('ExpirationDateLabel'),
				auditFieldName: 'dateExpiration',
				rowNumber: 1,
			}),
			{
				data: regulatoryDocumentDetails?.dateNewTypes,
				type: 'date',
				label: t('NewTypesLabel'),
				disclaimer: tDisclaimer,
				rowNumber: 2,
			},
			{
				data: regulatoryDocumentDetails?.dateNewRegistration,
				type: 'date',
				label: t('NewRegistrationLabel'),
				disclaimer: tDisclaimer,
				rowNumber: 2,
			},
			{
				data: regulatoryDocumentDetails?.phaseIn,
				type: 'phase',
				label: t('PhaseInLabel'),
				rowNumber: 3,
			},
			{
				data: regulatoryDocumentDetails?.phaseOut,
				type: 'phase',
				label: t('PhaseOutLabel'),
				rowNumber: 3,
			},
			createFieldThatSupportsHistory(showAudit, {
				data: regulatoryDocumentDetails?.modelYear,
				type: 'text',
				label: t('ModelYearLabel'),
				auditFieldName: '$.modelYear',
				rowNumber: 4,
			}),
		],
		[
			{
				data: documentReferencesOutgoing,
				type: 'references',
				label: t('DocumentReferences'),
				rowNumber: 1,
			},
			{
				data: documentReferencesIncoming,
				type: 'references',
				label: t('DocumentReferencedBy'),
				rowNumber: 1,
			},
			...(isGetexInjected
				? [
						{
							data: {
								header: t('GetexSectionHeader'),
								getexLinkLabel: 'Getex Link',
								getexLink: regulatoryDocumentDetails?.getexLink,
								keywordsLabel: t('KeywordsLabel'),
								keywords: regulatoryDocumentDetails.keywords,
								vehicleCategoriesLabel: t('VehicleCategoriesLabel'),
								vehicleCategories: vehicleCategories ?? [],
							},
							type: 'getexSection' as const,
							label: t('GetexSectionHeader'),
							rowNumber: 7,
						},
				  ]
				: []),
		],
	];
};
